import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { NotificationKind, NotificationTopic } from '@gobingebox/shared';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { adminApi } from './api';

const TOPICS: { id: NotificationTopic; label: string }[] = [
  { id: 'store', label: 'Store' },
  { id: 'games', label: 'Games' },
  { id: 'new_episodes', label: 'New episodes' },
];

const KINDS: { id: NotificationKind; label: string }[] = [
  { id: 'show', label: 'New show' },
  { id: 'premiere', label: 'Premiere' },
  { id: 'recommendation', label: 'Recommendation' },
  { id: 'system', label: 'System' },
];

function InAppBroadcast() {
  const [kind, setKind] = useState<NotificationKind>('show');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const send = useMutation({
    mutationFn: () => adminApi.sendInApp({ kind, title, body: body || undefined, link: link || undefined }),
    onSuccess: () => {
      setTitle('');
      setBody('');
      setLink('');
    },
  });

  return (
    <div className="card space-y-4 p-6">
      <div>
        <h3 className="font-display text-base font-bold text-ink">In-app notification</h3>
        <p className="mt-0.5 text-sm text-ink-muted">
          Shows in every member's notification bell — for new shows, premieres and picks.
        </p>
      </div>
      {send.isSuccess && <Alert tone="success">Pushed to {send.data.sent} members' inboxes.</Alert>}
      <label className="block text-sm text-ink-muted">
        Type
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as NotificationKind)}
          className="ml-2 h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink"
        >
          {KINDS.map((k) => (
            <option key={k.id} value={k.id}>{k.label}</option>
          ))}
        </select>
      </label>
      <Field label="Title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
      <div className="space-y-1.5">
        <label htmlFor="inapp-body" className="block text-sm font-medium text-ink">Message</label>
        <textarea
          id="inapp-body"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-brand"
        />
      </div>
      <Field label="Link (optional)" value={link} onChange={(e) => setLink(e.target.value)} placeholder="/shows/mate-or-die" />
      <Button onClick={() => send.mutate()} disabled={!title || send.isPending}>
        {send.isPending ? 'Sending…' : 'Push to all members'}
      </Button>
    </div>
  );
}

export function NotificationsAdmin() {
  const counts = useQuery({ queryKey: ['admin-notifications'], queryFn: adminApi.notificationCounts });
  const [topic, setTopic] = useState<NotificationTopic>('store');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const send = useMutation({
    mutationFn: () => adminApi.sendNotification(topic, subject, message),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        {TOPICS.map((t) => (
          <div key={t.id} className="card p-4 text-center">
            <p className="font-display text-2xl font-extrabold text-ink">
              {counts.data?.counts[t.id] ?? 0}
            </p>
            <p className="text-xs text-ink-faint">{t.label} opt-ins</p>
          </div>
        ))}
      </div>

      <InAppBroadcast />

      <div className="card space-y-4 p-6">
        <div>
          <h3 className="font-display text-base font-bold text-ink">Email announcement</h3>
          <p className="mt-0.5 text-sm text-ink-muted">Emails only members who opted into this topic.</p>
        </div>
        {send.isSuccess && (
          <Alert tone="success">
            Sent to {send.data.sent}/{send.data.recipients} subscribers.
            {send.data.sent === 0 && send.data.recipients > 0 && ' (Email isn’t configured yet — connect Resend to actually deliver.)'}
          </Alert>
        )}
        <label className="block text-sm text-ink-muted">
          Audience
          <select
            value={topic}
            onChange={(e) => setTopic(e.target.value as NotificationTopic)}
            className="ml-2 h-9 rounded-lg border border-border bg-surface-raised px-2 text-sm text-ink"
          >
            {TOPICS.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} opt-ins
              </option>
            ))}
          </select>
        </label>
        <Field label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={160} />
        <div className="space-y-1.5">
          <label htmlFor="notif-msg" className="block text-sm font-medium text-ink">Message</label>
          <textarea
            id="notif-msg"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm text-ink focus:border-brand"
          />
        </div>
        <Button onClick={() => send.mutate()} disabled={!subject || !message || send.isPending}>
          {send.isPending ? 'Sending…' : 'Send announcement'}
        </Button>
      </div>
    </div>
  );
}
